import React, { useState } from 'react';
import { toLocalDateString, parseLocalDate, getDayName, getTodayLocal } from '../utils/dateUtils';

interface Job {
  _id: string;
  title: string;
  schedule: {
    startDate: string;
    endDate: string;
    preferredTime: string;
  };
  visitSchedule?: Array<{
    date: string;
    isVisitDay: boolean;
    notes?: string;
    time?: string;
  }>;
  customer: {
    firstName: string;
    lastName: string;
  };
  contract: {
    type: string;
    rate: number;
  };
  status: string;
}

interface WorkScheduleCalendarProps {
  jobs: Job[];
}

const WorkScheduleCalendar: React.FC<WorkScheduleCalendarProps> = ({ jobs }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);

  // Get days in a month as calendar grid
  const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Check if a date has work scheduled
  const getWorkForDate = (date: Date): Array<Job & { dayNotes?: string; dayTime?: string }> => {
    const checkDate = parseLocalDate(date);
    const dateStr = toLocalDateString(checkDate);
    
    return jobs.filter(job => {
      const startDate = parseLocalDate(job.schedule.startDate);
      const endDate = parseLocalDate(job.schedule.endDate);
      
      // Check if date is within job period
      if (checkDate >= startDate && checkDate <= endDate) {
        // Check if this specific day is cancelled
        const visitScheduleEntry = job.visitSchedule?.find(vs => {
          // Extract date portion from backend ISO string or Date object
          const vsDateStr = toLocalDateString(vs.date);
          return vsDateStr === dateStr;
        });
        
        // If this day is explicitly cancelled, don't show work
        if (visitScheduleEntry && visitScheduleEntry.isVisitDay === false) {
          return false;
        }
        
        return true;
      }
      return false;
    }).map(job => {
      // Add notes and time to job if they exist
      const visitScheduleEntry = job.visitSchedule?.find(vs => {
        const vsDateStr = toLocalDateString(vs.date);
        return vsDateStr === dateStr;
      });
      return {
        ...job,
        dayNotes: visitScheduleEntry?.notes,
        dayTime: visitScheduleEntry?.time || job.schedule.preferredTime
      };
    });
  };

  // Navigate months
  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => {
      const previous = new Date(prev);
      previous.setMonth(previous.getMonth() - 1);
      return previous;
    });
  };

  // Check if date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if date is in the past
  const isPast = (date: Date): boolean => {
    const today = getTodayLocal();
    return date < today;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-900">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
        <button
          onClick={goToNextMonth}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
          <div key={i} className="text-xs font-semibold text-gray-500 text-center py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth()).map((date, i) => {
          if (!date) return <div key={i} className="aspect-square"></div>;
          
          const workScheduled = getWorkForDate(date);
          const hasWork = workScheduled.length > 0;
          const hasNotes = workScheduled.some(job => job.dayNotes);
          const isTodayDate = isToday(date);
          const isPastDate = isPast(date);
          
          return (
            <div
              key={i}
              onClick={() => {
                if (hasWork) {
                  setSelectedDate(date);
                  setShowNotesModal(true);
                }
              }}
              className={`aspect-square relative rounded-lg transition-all border-2 ${
                isTodayDate
                  ? 'border-primary-500 bg-primary-50'
                  : hasWork
                  ? 'border-green-300 bg-green-50 hover:bg-green-100 cursor-pointer'
                  : isPastDate
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
              title={hasWork ? `${workScheduled.length} job(s) scheduled${hasNotes ? ' - Has notes' : ''} - Click to view details` : 'No work scheduled'}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center p-1">
                <span className={`text-sm font-semibold ${
                  isTodayDate
                    ? 'text-primary-700'
                    : hasWork
                    ? 'text-green-800'
                    : isPastDate
                    ? 'text-gray-400'
                    : 'text-gray-700'
                }`}>
                  {date.getDate()}
                </span>
                {hasWork && (
                  <div className="flex flex-col items-center mt-1 w-full">
                    {workScheduled.slice(0, 2).map((job, idx) => (
                      <div
                        key={idx}
                        className="w-full h-1 bg-green-500 rounded-full mb-0.5"
                        title={`${job.customer.firstName} ${job.customer.lastName}`}
                      ></div>
                    ))}
                    {workScheduled.length > 2 && (
                      <span className="text-[8px] text-green-700 font-bold">+{workScheduled.length - 2}</span>
                    )}
                  </div>
                )}
                {hasNotes && (
                  <div className="absolute top-0.5 right-0.5">
                    <svg className="w-3 h-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                      <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-6 text-xs flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-primary-50 border-2 border-primary-500 rounded"></div>
          <span className="text-gray-600">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-50 border-2 border-green-300 rounded"></div>
          <span className="text-gray-600">Work Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border-2 border-gray-200 rounded"></div>
          <span className="text-gray-600">Free Day</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
            <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
          </svg>
          <span className="text-gray-600">Has Notes</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-50 border-2 border-gray-200 rounded"></div>
          <span className="text-gray-400">Cancelled Days</span>
        </div>
      </div>

      {/* Upcoming work summary */}
      {jobs.filter(job => {
        const endDate = new Date(job.schedule.endDate);
        return endDate >= new Date();
      }).length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">Active Contracts:</h4>
          <div className="space-y-2">
            {jobs
              .filter(job => {
                const endDate = new Date(job.schedule.endDate);
                return endDate >= new Date() && (job.status === 'in-progress' || job.status === 'accepted');
              })
              .map(job => (
                <div key={job._id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {job.customer.firstName} {job.customer.lastName}
                    </p>
                    <p className="text-xs text-gray-600">
                      {new Date(job.schedule.startDate).toLocaleDateString()} - {new Date(job.schedule.endDate).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-green-700">
                    ৳{job.contract.rate}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {getWorkForDate(selectedDate).map((job, idx) => (
                <div key={idx} className="border-2 border-green-200 bg-green-50 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{job.customer.firstName} {job.customer.lastName}</h4>
                      <p className="text-sm text-gray-600 mt-1 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {job.dayTime}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">
                      ৳{job.contract.rate}
                    </span>
                  </div>
                  
                  {job.dayNotes && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-yellow-800 mb-1">Special Notes:</p>
                          <p className="text-sm text-gray-700">{job.dayNotes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowNotesModal(false)}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkScheduleCalendar;
